import { useState, useEffect, useRef } from 'react';
import { Bell, Heart, Users, TrendingUp, CreditCard, Check, X, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { formatRelativeTime } from '../utils/displayHelpers';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Notification type icons
const typeIcons = {
  like: Heart,
  match: Users,
  stage_update: TrendingUp,
  subscription: CreditCard,
  system: Bell,
};

// Notification type colors
const typeColors = {
  like: 'text-pink-500 bg-pink-100 dark:bg-pink-900/30',
  match: 'text-green-500 bg-green-100 dark:bg-green-900/30',
  stage_update: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',
  subscription: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30',
  system: 'text-slate-500 bg-slate-100 dark:bg-slate-700/30',
};

export default function NotificationCenter() {
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications?limit=20`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/mark-read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.notification_id);
    }
    setIsOpen(false);
  };

  const bgPrimary = isDark ? 'bg-slate-800' : 'bg-white';
  const bgSecondary = isDark ? 'bg-slate-700' : 'bg-slate-50';
  const textPrimary = isDark ? 'text-white' : 'text-slate-900';
  const textSecondary = isDark ? 'text-slate-400' : 'text-slate-500';
  const borderColor = isDark ? 'border-slate-700' : 'border-slate-200';
  const hoverBg = isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-50';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button - always visible regardless of dark/light mode */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg transition-colors text-white hover:text-blue-300 hover:bg-slate-700"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full px-1"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div 
          className={`absolute right-0 mt-2 w-80 sm:w-96 ${bgPrimary} rounded-xl shadow-xl border ${borderColor} z-50 overflow-hidden`}
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className={`px-4 py-3 border-b ${borderColor} flex items-center justify-between`}>
            <h3 className={`font-semibold ${textPrimary}`}>Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-blue-500 hover:text-blue-600 font-medium"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={32} className={`mx-auto mb-2 ${textSecondary}`} />
                <p className={textSecondary}>No notifications yet</p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => {
                  const Icon = typeIcons[notification.type] || Bell;
                  const colorClasses = typeColors[notification.type] || typeColors.system;
                  
                  return (
                    <div
                      key={notification.notification_id}
                      className={`relative ${!notification.read ? bgSecondary : ''}`}
                    >
                      {notification.action_link ? (
                        <Link
                          to={notification.action_link}
                          onClick={() => handleNotificationClick(notification)}
                          className={`block px-4 py-3 ${hoverBg} transition-colors`}
                        >
                          <NotificationContent 
                            notification={notification}
                            Icon={Icon}
                            colorClasses={colorClasses}
                            textPrimary={textPrimary}
                            textSecondary={textSecondary}
                          />
                        </Link>
                      ) : (
                        <div 
                          className={`px-4 py-3 ${hoverBg} transition-colors cursor-pointer`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <NotificationContent 
                            notification={notification}
                            Icon={Icon}
                            colorClasses={colorClasses}
                            textPrimary={textPrimary}
                            textSecondary={textSecondary}
                          />
                        </div>
                      )}
                      
                      {/* Unread indicator */}
                      {!notification.read && (
                        <div className="absolute left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className={`px-4 py-2 border-t ${borderColor}`}>
              <Link
                to="/notifications"
                onClick={() => setIsOpen(false)}
                className={`text-sm text-blue-500 hover:text-blue-600 font-medium flex items-center justify-center gap-1`}
              >
                View all notifications
                <ChevronRight size={14} />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Notification content component
function NotificationContent({ notification, Icon, colorClasses, textPrimary, textSecondary }) {
  return (
    <div className="flex gap-3">
      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${colorClasses}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${textPrimary} line-clamp-1`}>
          {notification.title}
        </p>
        <p className={`text-sm ${textSecondary} line-clamp-2`}>
          {notification.message}
        </p>
        <p className={`text-xs ${textSecondary} mt-1`}>
          {formatRelativeTime(notification.created_at)}
        </p>
      </div>
    </div>
  );
}

// Hook to refresh notifications externally
export function useNotifications() {
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return { unreadCount, refresh };
}
